import { produceSecureUrl } from "@screencloud/studio-media-client";
// buildURL removed - using getResizedMedia for secureMediaUrl instead
import { FormattedMessage } from "react-intl";
import { Icon } from "@screencloud/screencloud-ui-components";
import {
  File,
  FileListItemForPlaylistFragment,
  FileListItemFragment,
  FileOutput,
  JobStatus,
  Maybe,
  Scalars,
} from "../../src/types.g";
import { appConfig } from "../appConfig";
import { MediaType, NameMaxLengthAllow } from "../constants/constants";
import { ssm } from "../state/session/ssm";
import queryHelper from "../state/helper/query";
import Thumbnail from "../components/MediaThumbnail";
import failedProcessingThumbnail from "../images/processing_failure_thumb.svg";
import imagePlaceholder from "../images/image_thumb_placeholder.svg";
import videoPlaceholder from "../images/video_thumb_placeholder.svg";
import documentPlaceholder from "../images/document_thumb_placeholder.svg";
import audioPlaceholder from "../images/audio_thumb_placeholder.svg";
import { getTheUsedPlace } from "./deleteStuffHelper";
import DeleteWarning from "../components/DeleteWarning";
import { ResizingMode } from "src/constants/mediaTypes";

export { failedProcessingThumbnail };
export { imagePlaceholder };

export interface FSProgressEvent {
  totalPercent: number;
  totalBytes: number;
}

/**
 * Looks through the fileOutputs of a given File and returns a image FileOutput
 */
export const getFileOutputImageSet = (
  media: Partial<File> | FileListItemFragment | FileListItemForPlaylistFragment,
) => {
  return (
    media.fileOutputsByFileId &&
    media.fileOutputsByFileId.nodes.find((fileOutput) => isImage(fileOutput!))
  );
};

/**
 * Looks through the fileOutputs of a given File and returns a video FileOutput
 * if it exists.
 * NB: This video will be the transcoded variant of the original video file.
 */
export const getFileOutputVideo = (media: Partial<File>) => {
  return (
    media.fileOutputsByFileId &&
    media.fileOutputsByFileId.nodes.find((fileOutput) => isVideo(fileOutput!))
  );
};

export const getFileOutputs4kVideoDetail = (media: Partial<File>) => {
  const fileOutputs =
    media.fileOutputsByFileId &&
    media.fileOutputsByFileId.nodes.find(
      (fileOutput) => fileOutput.mimetype === "application/dash+xml",
    );
  const metadata = fileOutputs?.metadata?.filter(
    (d) => d.videoDetails?.widthInPx || d.videoDetails?.heightInPx,
  );
  const result = Object.values(
    (metadata ?? []).reduce((r, o) => {
      r[o.widthInPx] =
        r[o.widthInPx] && r[o.widthInPx].widthInPx > o.widthInPx ? r : o;
      return r;
    }, {} as FileOutput),
  ) as FileOutput[] | undefined;
  const fileoutputTmp = fileOutputs && { ...fileOutputs };
  if (fileoutputTmp?.metadata) {
    fileoutputTmp.metadata =
      Array.isArray(result) && result.length > 0
        ? result[0]
        : fileOutputs?.metadata;
  }
  return fileoutputTmp;
};

export const getFileOutputAudio = (media: Partial<File>) => {
  return (
    media.fileOutputsByFileId &&
    media.fileOutputsByFileId.nodes.find((fileOutput) => isAudio(fileOutput!))
  );
};

export const getFileDuration = (
  media: Partial<File>,
  defaultDuration: number,
): number => {
  const videoFileOutput = getFileOutputVideo(media);
  return videoFileOutput?.metadata?.duration
    ? videoFileOutput.metadata.duration
    : defaultDuration;
};

export const getFileAudioDuration = (
  media: Partial<File>,
  defaultDuration: number,
): number => {
  const videoFileOutput = getFileOutputAudio(media);
  return videoFileOutput?.metadata?.duration
    ? videoFileOutput.metadata.duration
    : defaultDuration;
};

interface HasMimeType {
  mimetype?: Maybe<Scalars["String"]>;
}

export const isDocument = (media: HasMimeType) =>
  ["text", "application"].some((documentType) =>
    (media.mimetype ?? "").startsWith(documentType),
  );

export const isVideo = (media: HasMimeType) =>
  (media?.mimetype ?? "").startsWith("video");

export const isImage = (media: HasMimeType) =>
  (media?.mimetype ?? "").startsWith("image");

export const isAudio = (media: HasMimeType) =>
  (media?.mimetype ?? "").startsWith("audio");

export const isMediaWithAudio = (media?: HasMimeType) =>
  media && (isAudio(media) || isVideo(media));

export const getMediaType = (media?: Pick<HasMimeType, "mimetype">) => {
  if (media && media.mimetype) {
    if (isDocument(media)) {
      return MediaType.DOCUMENT;
    }
    if (isImage(media)) {
      return MediaType.IMAGE;
    }
    if (isAudio(media)) {
      return MediaType.AUDIO;
    }
    if (isVideo(media)) {
      return MediaType.VIDEO;
    }
  }
  return MediaType.UNKNOWN;
};

export const getPlaceholderThumbnail = (mediaType: MediaType) => {
  switch (mediaType) {
    case MediaType.IMAGE:
      return imagePlaceholder;
    case MediaType.AUDIO:
      return audioPlaceholder;
    case MediaType.DOCUMENT:
      return documentPlaceholder;
    case MediaType.VIDEO:
      return videoPlaceholder;
  }
};

/**
 * Generates a secure media thumbnail URL with proper resizing and authentication
 * Handles complex edge cases like processing states, file outputs, and placeholders
 *
 * @param media The media object containing file information
 * @param width Desired thumbnail width
 * @param height Desired thumbnail height
 * @param secureMediaUrlPolicy Security policy for authentication
 * @param defaultPlaceHoldertype Optional placeholder type for failed/pending media
 * @param fit Resize fit mode (default: "cover")
 * @returns Secure thumbnail URL or placeholder
 */
export const generateMediaThumbnailUrl = (
  media: MediaInput,
  width: number,
  height: number,
  secureMediaUrlPolicy: string | undefined,
  defaultPlaceHoldertype?: MediaType,
  fit?: ResizingMode,
): string => {
  const nodes =
    media?.fileOutputsByFileId && media.fileOutputsByFileId.nodes
      ? media.fileOutputsByFileId.nodes
      : [];
  const mediaImageKey = media?.metadata?.key;

  // Handle failed processing
  if (media?.fileProcessingStatus === JobStatus.Failed) {
    return failedProcessingThumbnail;
  }

  // Handle completed original files
  if (media?.fileProcessingStatus === JobStatus.Completed && mediaImageKey) {
    const fileThumbnail = buildSecureMediaUrl({
      orgId: ssm.current.claims.orgId,
      mediaPath: mediaImageKey,
      width,
      height,
      fit: fit || "cover",
      secureMediaPolicy: secureMediaUrlPolicy,
    });

    // For images, return the built URL directly
    if (isImage(media)) {
      return fileThumbnail;
    }

    // For videos/docs, continue to check for processed outputs
    if (!nodes.length) {
      // No processed outputs yet, return placeholder
      return defaultPlaceHoldertype
        ? getPlaceholderThumbnail(defaultPlaceHoldertype)
        : isVideo(media)
          ? videoPlaceholder
          : isAudio(media)
            ? audioPlaceholder
            : documentPlaceholder;
    }
  }

  // Handle processed file outputs (video thumbnails, document previews)
  if (nodes.length) {
    const processedFile = nodes.find((fileOutput) => isImage(fileOutput!));

    if (processedFile?.content.keys) {
      // Use the processed thumbnail with resizing and security
      return secureAndResizeUrl({
        url: `${appConfig.secureMediaUrl}/${processedFile.content.keys[0]}`,
        width,
        height,
        fit: fit || "cover",
        secureMediaPolicy: secureMediaUrlPolicy,
      });
    }

    // Processed file exists but no keys - return placeholder
    return defaultPlaceHoldertype
      ? getPlaceholderThumbnail(defaultPlaceHoldertype)
      : isVideo(media)
        ? videoPlaceholder
        : media && isAudio(media)
          ? audioPlaceholder
          : documentPlaceholder;
  }

  // Fallback to placeholder for pending/unknown states
  return imagePlaceholder;
};

/**
 * @deprecated Use generateMediaThumbnailUrl instead. This function name is misleading.
 * Kept for backward compatibility with existing code.
 */
export const generateImgixURL = generateMediaThumbnailUrl;

/**
 * Generates a secure media thumbnail with standard dimensions
 *
 * @param media The media object
 * @param secureMediaUrlPolicy Security policy for authentication
 * @param small If true, uses 60x60 dimensions, otherwise 320x180
 * @param defaultPlaceholderType Optional placeholder type
 * @returns Secure thumbnail URL or placeholder
 */
export const generateMediaThumbnail = (
  media: MediaInput,
  secureMediaUrlPolicy: string | undefined,
  small: boolean = false,
  defaultPlaceholderType?: MediaType,
) => {
  const dimensions = small ? { w: 60, h: 60 } : { w: 320, h: 180 };
  return generateMediaThumbnailUrl(
    media,
    dimensions.w,
    dimensions.h,
    secureMediaUrlPolicy,
    defaultPlaceholderType,
  );
};

/**
 * @deprecated Use generateMediaThumbnail instead. This function name is misleading.
 * Kept for backward compatibility with existing code.
 */
export const generateImgixThumbnail = generateMediaThumbnail;

export const getPreviewImageURLFromFile = (
  media: MediaInput,
  width: number = 320,
  height: number = 180,
  secureMediaUrlPolicy: string | undefined,
  fit: ResizingMode = ResizingMode.Inside,
): string | string[] => {
  return generateImgixURL(
    media,
    width,
    height,
    secureMediaUrlPolicy,
    undefined,
    fit,
  );
};

export const getMediaPlaceHolder = (media: HasMimeType): string => {
  if (!media) {
    return "";
  } else if (isVideo(media)) {
    return videoPlaceholder;
  } else if (isAudio(media)) {
    return audioPlaceholder;
  } else if (isDocument(media)) {
    return documentPlaceholder;
  } else if (isImage(media)) {
    return imagePlaceholder;
  } else {
    return "";
  }
};

export const getImageDimensions = async (handle: string) => {
  const imageSizeURL = `https://cdn.filestackcontent.com/imagesize/security=policy:${appConfig.fileStackSecurityPolicy},signature:${appConfig.fileStackSecuritySignature}/${handle}`;
  return fetch(imageSizeURL);
};

// We use a 120-char length database constraint on media file names to display
// more nicely
export const trimToLessThan80Char = (fileName: string) => {
  return fileName.trim().substring(0, NameMaxLengthAllow).trim();
};

export const getMediaTypeText = (media) => {
  const mediaType = getMediaType(media);
  switch (mediaType) {
    case MediaType.DOCUMENT:
      return "Document";
    case MediaType.IMAGE:
      return "Image";
    case MediaType.AUDIO:
      return "Audio";
    case MediaType.VIDEO:
      return "Video";
    default:
      return "";
  }
};

export const getMediaTypeTextFormatted = (media) => {
  const mediaType = getMediaType(media);
  switch (mediaType) {
    case MediaType.DOCUMENT:
      return (
        <FormattedMessage defaultMessage="Document" id="media.type.document" />
      );
    case MediaType.IMAGE:
      return <FormattedMessage defaultMessage="Image" id="media.type.image" />;
    case MediaType.AUDIO:
      return <FormattedMessage defaultMessage="Audio" id="media.type.audio" />;
    case MediaType.VIDEO:
      return <FormattedMessage defaultMessage="Video" id="media.type.video" />;
    default:
      return "";
  }
};

export const is4KAvailable = (media: Partial<File>): boolean => {
  const getFileDetail = getFileOutputs4kVideoDetail(media);
  const details = getFileDetail?.metadata.videoDetails;
  return details && Math.max(details.widthInPx, details.heightInPx) > 1920;
};

export function isFolder(media: { __typename: string }) {
  return media.__typename === "Folder";
}

export function isFile(media: { __typename: string }) {
  return media.__typename === "File";
}

export const getDeleteMediaConfirmMsg = async (media: File) => {
  const file = await queryHelper.getFileAssociation(media.id);
  let message = (
    <>
      <h2>
        <FormattedMessage
          id="ui_component.confirm.ui_component.label.move_to_trash_question"
          defaultMessage="Move to Trash?"
          values={{ name: media.name }}
        />
      </h2>
      <p>
        <FormattedMessage
          id="ui_component.confirm.move_to_trash_message_with_cast"
          defaultMessage="{name} will be deleted and moved to the Trash."
          values={{
            name: (
              <strong
                className="delete-text"
                style={{ overflowWrap: "break-word" }}
              >
                {media.name}
              </strong>
            ),
          }}
        />
      </p>
    </>
  );
  if (
    (file?.data.fileById?.associationsByToFileAndOrgId.totalCount ?? 0 > 0) &&
    media.__typename === "File"
  ) {
    const places = getTheUsedPlace(
      file?.data.fileById?.associationsByToFileAndOrgId.nodes,
    );
    message = (
      <DeleteWarning name={media.name} places={places} isSoftDelete={true} />
    );
  }
  return message;
};

export const getContentThumbnail = (
  contentType: string,
  contentData: any,
  secureMediaPolicy: string | undefined,
) => {
  switch (contentType) {
    case "app":
      return (
        <div
          className="thumbnail app-thumbnail"
          style={{ backgroundImage: `url(${contentData.thumbnail})` }}
        />
      );
    case "link":
    case "file":
      return (
        <Thumbnail
          media={contentData.thumbnail}
          height={100}
          width={100}
          secureMediaPolicy={secureMediaPolicy}
        />
      );
    case "playlist":
      return (
        <div
          className="thumbnail playlist-thumbnail"
          style={{ backgroundColor: contentData.thumbnail }}
        >
          <Icon name="playlist" />
        </div>
      );
    case "channel":
      const channelData = contentData.thumbnail;
      const result =
        channelData?.coverImageId && channelData?.fileByCoverImageId
          ? generateImgixURL(
              channelData?.fileByCoverImageId,
              600,
              600,
              secureMediaPolicy,
            )
          : `linear-gradient(-45deg, ${channelData?.coverData.color.top} 0%, ${channelData?.coverData.color.bottom} 100%)`;
      const coverStyle = result.includes("http")
        ? { backgroundImage: `url(${result})` }
        : { backgroundImage: result };
      return <div className="thumbnail channel-thumbnail" style={coverStyle} />;
    default:
      return;
  }
};

export type MediaInput = Pick<
  File,
  "metadata" | "fileProcessingStatus" | "mimetype" | "source"
> & {
  fileOutputsByFileId: {
    nodes: Pick<FileOutput, "content" | "mimetype">[];
  };
};

/**
 * Extracts organization ID from a media URL
 * Handles various URL patterns including:
 * - /orgId/originals/
 * - /orgId/processed/
 * - /orgId/NxN_fit/
 * - /orgId/something/
 */
export const extractOrgIdFromPath = (url: string): string | null => {
  // Match orgId from various URL patterns
  const patterns = [
    /\/([^/]+)\/originals\//, // matches /orgId/originals/
    /\/([^/]+)\/originals\//, // matches /orgId/processed/
    /\/([^/]+)\/\d+x\d+_[^/]+\//, // matches /orgId/NxN_fit/
    /\/([^/]+)\//, // fallback: matches /orgId/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
};

export const replaceWithSecureURL = (
  url: string,
  height?: number | undefined,
  width?: number | undefined,
  fit: string = "cover",
) => {
  // list of valid screen cloud media origins
  const validOrigins = [
    `${appConfig.secureMediaUrl}`,
    `${appConfig.mediaOrigin}`,
  ];

  const reg = new RegExp("^https?://[^#?/]+");

  if (validOrigins.filter((domain) => url.includes(domain)).length > 0) {
    return url.replace(reg, appConfig.secureMediaUrl);
  }

  // add height and width in place of original
  height && width && url.replace("originals", `${height}x${width}_${fit}`);

  return url;
};

/**
 * returns resized media url
 */

export const getResizedMedia = (
  media: string,
  height: number,
  width: number,
  fit = "cover",
) => {
  return media.replace("originals", `${width}x${height}_${fit}`);
};

/**
 * Unified helper to build, resize, and secure media URLs
 * Handles the common pattern of: build URL -> resize -> add auth
 *
 * @param options Configuration object
 * @param options.orgId Organization ID
 * @param options.mediaPath Path to media (e.g., "originals/file.jpg" or full path from keys[0])
 * @param options.width Desired width
 * @param options.height Desired height
 * @param options.fit Resize fit mode (default: "cover")
 * @param options.secureMediaPolicy Optional security policy for authentication
 * @returns Fully processed secure media URL
 */
export const buildSecureMediaUrl = ({
  orgId,
  mediaPath,
  width,
  height,
  fit = "cover",
  secureMediaPolicy,
}: {
  orgId: string;
  mediaPath: string;
  width: number;
  height: number;
  fit?: string;
  secureMediaPolicy?: string | null;
}): string => {
  // Build base URL - handle both full paths and relative paths
  const basePath = mediaPath.startsWith("originals/")
    ? mediaPath
    : `originals/${encodeURIComponent(mediaPath)}`;

  const baseUrl = `${appConfig.secureMediaUrl}/${orgId}/${basePath}`;

  // Apply resizing
  const resizedUrl = getResizedMedia(baseUrl, height, width, fit);

  // Apply security policy if provided
  if (secureMediaPolicy) {
    return produceSecureUrl({
      baseUrl: resizedUrl,
      policy: secureMediaPolicy,
    });
  }

  return resizedUrl;
};

/**
 * Simplified version for when you already have a full URL
 * Just applies resizing and security policy
 */
export const secureAndResizeUrl = ({
  url,
  width,
  height,
  fit = "cover",
  secureMediaPolicy,
}: {
  url: string;
  width: number;
  height: number;
  fit?: string;
  secureMediaPolicy?: string | null;
}): string => {
  // Apply resizing
  const resizedUrl = getResizedMedia(url, height, width, fit);

  // Apply security policy if provided
  if (secureMediaPolicy) {
    return produceSecureUrl({
      baseUrl: resizedUrl,
      policy: secureMediaPolicy,
    });
  }

  return resizedUrl;
};

export const getSecureCanvasThumbnailUrl = (
  sourceUrl: string,
  secureMediaPolicy?: string,
  callerOrgId?: string,
) => {
  // Only check org ID if one was provided
  if (callerOrgId) {
    const orgIdFromUrl = extractOrgIdFromPath(
      sourceUrl.replace(appConfig.mediaOrigin, ""),
    );

    // Return original URL if org IDs don't match
    if (callerOrgId !== orgIdFromUrl) {
      return sourceUrl;
    }
  }

  // Return original URL if no secure policy provided
  if (!secureMediaPolicy) {
    return sourceUrl;
  }

  // Generate secure URL with thumbnail dimensions
  return replaceWithSecureURL(
    produceSecureUrl({
      baseUrl: sourceUrl,
      policy: secureMediaPolicy,
    }),
    320,
    180,
    "clip",
  );
};
